import type { HostedAgentSummary } from "@/api/client/hosted-agents"

import { cn } from "@/lib/utils"

import { AgentCharacter } from "./agent-character"
import { useAgentMotion } from "../hooks/use-agent-motion"
import { agentCharacterIds } from "../utils/agent-character-utils"
import {
  agentCharacter,
  type AgentPresence,
  type WorkingActivityKind,
} from "../utils/agent-presence-utils"

type AgentAvatarProps = {
  agent: HostedAgentSummary
  /** Tailwind size classes, e.g. "size-12". */
  className?: string
  presence?: AgentPresence
  celebrate?: boolean
  activityKind?: WorkingActivityKind | null
  attention?: boolean
  compact?: boolean
}

/** Stable identity, shared by roster, detail, greeting and textual chat presence. */
export function AgentAvatar({
  agent,
  className,
  presence = "idle",
  celebrate = false,
  activityKind = null,
  attention = false,
  compact = false,
}: AgentAvatarProps) {
  const fallback = agentCharacterIds[agentCharacter(agent.id)]
  const requested = agent.profile.avatar.character
  const character = requested && agentCharacterIds.includes(requested) ? requested : fallback
  const motion = useAgentMotion()
  return (
    <div
      aria-hidden="true"
      data-character={character}
      data-presence={presence}
      className={cn(
        "relative flex size-12 shrink-0 items-center justify-center select-none",
        className
      )}
    >
      <AgentCharacter
        activity={activityKind}
        attention={attention}
        celebrate={celebrate}
        character={character}
        className="size-full"
        compact={compact}
        paused={motion.paused}
        state={presence}
        still={["stopped", "stopping", "reconnecting", "loading"].includes(presence)}
      />
    </div>
  )
}
